import { getSetting } from './settings';

// 常用时区列表
export const TIMEZONE_OPTIONS = [
  { value: 'Asia/Shanghai', label: '中国标准时间 (UTC+8)', offset: '+08:00' },
  { value: 'America/New_York', label: '美国东部时间 (UTC-5/-4)', offset: '-05:00/-04:00' },
  { value: 'America/Los_Angeles', label: '美国西部时间 (UTC-8/-7)', offset: '-08:00/-07:00' },
  { value: 'Europe/London', label: '英国时间 (UTC+0/+1)', offset: '+00:00/+01:00' },
  { value: 'Europe/Paris', label: '欧洲中部时间 (UTC+1/+2)', offset: '+01:00/+02:00' },
  { value: 'Asia/Tokyo', label: '日本标准时间 (UTC+9)', offset: '+09:00' },
  { value: 'Asia/Seoul', label: '韩国标准时间 (UTC+9)', offset: '+09:00' },
  { value: 'Asia/Kolkata', label: '印度标准时间 (UTC+5:30)', offset: '+05:30' },
  { value: 'Australia/Sydney', label: '澳大利亚东部时间 (UTC+10/+11)', offset: '+10:00/+11:00' },
  { value: 'UTC', label: '协调世界时 (UTC)', offset: '+00:00' },
];

/**
 * 将UTC时间转换为指定时区的时间
 * @param utcDate UTC时间字符串或Date对象
 * @param timezone 目标时区，如果不提供则使用用户设置的时区
 * @returns 转换后的Date对象
 */
export function convertToTimezone(utcDate: string | Date, timezone?: string): Date {
  const targetTimezone = timezone || getSetting('timezone');
  
  // 确保输入是Date对象
  const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
  
  // 如果日期无效，返回当前时间
  if (isNaN(date.getTime())) {
    console.warn('无效的日期:', utcDate);
    return new Date();
  }
  
  // 使用Intl.DateTimeFormat进行时区转换
  try {
    // 获取目标时区的时间
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: targetTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    const parts = formatter.formatToParts(date);
    const partsObj = parts.reduce((acc, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {} as Record<string, string>);
    
    // 构造新的Date对象（注意：这里构造的是本地时间，但值是目标时区的时间）
    const convertedDate = new Date(
      parseInt(partsObj.year),
      parseInt(partsObj.month) - 1, // 月份从0开始
      parseInt(partsObj.day),
      parseInt(partsObj.hour),
      parseInt(partsObj.minute),
      parseInt(partsObj.second)
    );
    
    return convertedDate;
  } catch (error) {
    console.error('时区转换失败:', error, '使用原始时间');
    return date;
  }
}

/**
 * 格式化日期时间，自动应用用户设置的时区
 * @param date 日期字符串或Date对象
 * @param options 格式化选项
 * @returns 格式化后的字符串
 */
export function formatDateTime(
  date: string | Date,
  options: {
    showTime?: boolean;
    showSeconds?: boolean;
    showTimezone?: boolean;
    timezone?: string;
    format?: 'short' | 'medium' | 'long' | 'full';
  } = {}
): string {
  const {
    showTime = true,
    showSeconds = false,
    showTimezone = false,
    timezone,
    format = 'medium'
  } = options;
  
  const targetTimezone = timezone || getSetting('timezone');
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) {
    return '无效日期';
  }
  
  try {
    const formatOptions: Intl.DateTimeFormatOptions = {
      timeZone: targetTimezone,
      year: 'numeric',
      month: format === 'short' ? 'numeric' : format === 'medium' ? 'short' : 'long',
      day: 'numeric',
    };
    
    if (showTime) {
      formatOptions.hour = 'numeric';
      formatOptions.minute = '2-digit';
      formatOptions.hour12 = false;
      
      if (showSeconds) {
        formatOptions.second = '2-digit';
      }
    }
    
    if (showTimezone) {
      formatOptions.timeZoneName = 'short';
    }
    
    return new Intl.DateTimeFormat('zh-CN', formatOptions).format(dateObj);
  } catch (error) {
    console.error('日期格式化失败:', error);
    return dateObj.toLocaleString('zh-CN');
  }
}

/**
 * 格式化相对时间（如：2小时前、昨天等）
 * @param date 日期字符串或Date对象
 * @param timezone 时区
 * @returns 相对时间字符串
 */
export function formatRelativeTime(date: string | Date, timezone?: string): string {
  const targetTimezone = timezone || getSetting('timezone');
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  
  if (isNaN(dateObj.getTime())) {
    return '无效日期';
  }
  
  // 转换到目标时区进行比较
  const convertedDate = convertToTimezone(dateObj, targetTimezone);
  const convertedNow = convertToTimezone(now, targetTimezone);
  
  const diffMs = convertedNow.getTime() - convertedDate.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffMinutes < 1) {
    return '刚刚';
  } else if (diffMinutes < 60) {
    return `${diffMinutes}分钟前`;
  } else if (diffHours < 24) {
    return `${diffHours}小时前`;
  } else if (diffDays === 1) {
    return '昨天';
  } else if (diffDays < 7) {
    return `${diffDays}天前`;
  } else {
    // 超过一周显示具体日期
    return formatDateTime(dateObj, { 
      showTime: false, 
      format: 'short',
      timezone: targetTimezone 
    });
  }
}

/**
 * 获取当前用户时区的当前时间
 * @returns Date对象
 */
export function getCurrentTime(): Date {
  const timezone = getSetting('timezone');
  return convertToTimezone(new Date(), timezone);
}

/**
 * 检查时区是否有效
 * @param timezone 时区字符串
 * @returns 是否有效
 */
export function isValidTimezone(timezone: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

/**
 * 获取时区的显示名称
 * @param timezone 时区字符串
 * @returns 显示名称
 */
export function getTimezoneDisplayName(timezone: string): string {
  const option = TIMEZONE_OPTIONS.find(opt => opt.value === timezone);
  if (option) {
    return option.label;
  }
  
  try {
    // 尝试获取系统的时区名称
    const formatter = new Intl.DateTimeFormat('zh-CN', {
      timeZone: timezone,
      timeZoneName: 'long'
    });
    const parts = formatter.formatToParts(new Date());
    const timeZonePart = parts.find(part => part.type === 'timeZoneName');
    return timeZonePart ? timeZonePart.value : timezone;
  } catch {
    return timezone;
  }
} 